import * as XLSX from "xlsx";
import JSZip from "jszip";
import axios from "axios";
import config from "../../config.json";

function authHeaders() {
  const token = localStorage.getItem("tokenLogin");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function sanitizeSheetName(name) {
  let s = String(name || "Sheet").slice(0, 31);
  s = s.replace(/[:\/\\?\*\[\]]/g, "-");
  return s.trim() || "Sheet";
}

function stripHtml(html) {
  if (html == null || typeof html !== "string") return "";
  const tmp = typeof document !== "undefined" ? document.createElement("div") : null;
  if (tmp) {
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
  }
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function joinLangDetails(arr, field) {
  if (!Array.isArray(arr)) return "";
  return arr
    .map((d) => {
      const v = d?.[field];
      if (v == null || v === "") return "";
      return `${d.language || "?"}: ${stripHtml(String(v))}`;
    })
    .filter(Boolean)
    .join(" | ");
}

function formatExtraSizes(extraSizes) {
  if (!Array.isArray(extraSizes) || !extraSizes.length) return "";
  return extraSizes.map((s) => `${s.size || "?"}=${s.finalPrice ?? ""}`).join("; ");
}

function unwrapProduct(ref) {
  if (!ref) return null;
  if (ref.product) return ref.product;
  return ref;
}

function productIdOf(p) {
  if (!p) return "";
  const id = p._id ?? p;
  return id != null ? String(id) : "";
}

function safeFilenamePart(s) {
  return String(s || "export")
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-")
    .slice(0, 80);
}

function safeImageFileSegment(name) {
  return String(name || "file").replace(/[^a-zA-Z0-9._-]+/g, "_");
}

function extensionFromFilename(name) {
  const m = String(name).match(/(\.[a-zA-Z0-9]{2,5})$/);
  return m ? m[1].toLowerCase() : ".webp";
}

function uploadsUrl(relativePath) {
  const rel = String(relativePath || "").replace(/^\/+/, "");
  if (!rel) return "";
  const base = (config.UPLOADS_SERVER || "").replace(/\/+$/, "");
  return `${base}/${rel}`;
}

/** Zip paths: images/menu/..., images/category/..., etc. */
class HierarchyImageCollector {
  constructor() {
    this.seenUrls = new Set();
    /** @type {Map<string, Set<string>>} */
    this.usedNames = new Map();
    /** @type {{ url: string, zipPath: string, entityType: string, entityId: string, sourceFile: string }[]} */
    this.tasks = [];
  }

  /**
   * @param {string} relativeUploadPath e.g. menu/foo.webp, product/bar.png
   * @param {string} entityId
   * @param {string} [preferredBase]
   */
  add(relativeUploadPath, entityId, preferredBase) {
    if (!relativeUploadPath) return;
    const rel = String(relativeUploadPath).replace(/^\/+/, "");
    const url = uploadsUrl(rel);
    if (!url || this.seenUrls.has(url)) return;
    this.seenUrls.add(url);

    const firstSeg = rel.split("/")[0] || "misc";
    const folder = ["menu", "category", "product", "items", "user"].includes(firstSeg)
      ? firstSeg
      : "misc";

    const sourceFile = rel.split("/").pop() || "file";
    const ext = extensionFromFilename(sourceFile);
    let base =
      safeImageFileSegment(preferredBase || sourceFile).replace(/\.[a-zA-Z0-9]+$/, "") || "image";
    const idPart = safeFilenamePart(entityId).slice(0, 24);
    let fileName = `${idPart}__${base}${ext}`;

    if (!this.usedNames.has(folder)) this.usedNames.set(folder, new Set());
    const set = this.usedNames.get(folder);
    let n = 0;
    while (set.has(fileName)) {
      n += 1;
      fileName = `${idPart}__${base}_${n}${ext}`;
    }
    set.add(fileName);

    this.tasks.push({
      url,
      zipPath: `images/${folder}/${fileName}`,
      entityType: folder,
      entityId: String(entityId || ""),
      sourceFile,
    });
  }
}

function flattenDoc(doc) {
  if (!doc || typeof doc !== "object") return { note: "empty" };
  const row = {};
  for (const k of Object.keys(doc)) {
    const v = doc[k];
    if (v === undefined) continue;
    if (v === null) {
      row[k] = "";
      continue;
    }
    if (typeof v === "object") row[k] = JSON.stringify(v);
    else row[k] = v;
  }
  return row;
}

function menuNameEnOf(menu) {
  return (
    menu?.menuDetails?.find((d) => d.language === "en")?.menuName ||
    menu?.menuDetails?.[0]?.menuName ||
    String(menu?._id || "")
  );
}

/**
 * Walk one full menu document; append rows and register images used in structure.
 */
function walkMenuStructure(menu, byProductId, byChoiceId, images) {
  const menuNameEn = menuNameEnOf(menu);
  const menuId = String(menu._id || "");

  if (menu.photo) {
    images.add(`menu/${menu.photo}`, menuId, menu.photo);
  }

  const categoryRows = [];
  const productRows = [];

  const categoriesList = Array.isArray(menu.categoriesList) ? menu.categoriesList : [];

  for (const catItem of categoriesList) {
    const cat = catItem.category;
    if (!cat) continue;

    const catTitles = joinLangDetails(cat.categoryDetails, "categoryTitle");
    const catId = String(cat._id || "");
    const catOrder = catItem.order ?? "";

    if (cat.photo) {
      images.add(`category/${cat.photo}`, catId, cat.photo);
    }

    categoryRows.push({
      menuId,
      menuNameEN: menuNameEn,
      categoryOrder: catOrder,
      categoryId: catId,
      categoryTitlesAllLangs: catTitles,
      categoryPhoto: cat.photo || "",
      subcategoryOrder: "",
      subcategoryId: "",
      subcategoryTitlesAllLangs: "",
      placement: "category",
    });

    const directProducts = Array.isArray(catItem.productList) ? catItem.productList : [];
    for (const pl of directProducts) {
      const pop = unwrapProduct(pl.product);
      const pid = productIdOf(pop);
      if (!pid) continue;
      const catalog = byProductId.get(pid) || {};
      const merged = { ...pop, ...catalog };
      const order = pl.order ?? "";

      const choiceRefs = Array.isArray(merged.choices) ? merged.choices : [];
      if (Array.isArray(merged.photos)) {
        merged.photos.forEach((fn, idx) => {
          if (fn) images.add(`product/${fn}`, pid, `${pid}_photo_${idx}_${fn}`);
        });
      }

      productRows.push({
        menuId,
        menuNameEN: menuNameEn,
        placement: "Category (direct)",
        categoryOrder: catOrder,
        categoryId: catId,
        categoryTitles: catTitles,
        subcategoryOrder: "",
        subcategoryId: "",
        subcategoryTitles: "",
        productOrder: order,
        productId: pid,
        productTitlesAllLangs: joinLangDetails(merged.productDetails, "productTitle"),
        descriptionsAllLangs: joinLangDetails(merged.productDetails, "productDescription"),
        price: merged.price ?? "",
        rate: merged.rate ?? "",
        haveMultipleSize: !!merged.haveMultipleSize,
        extraSizes: formatExtraSizes(merged.extraSizes),
        tags: Array.isArray(merged.tags) ? merged.tags.join(", ") : "",
        photos: Array.isArray(merged.photos) ? merged.photos.join(", ") : "",
        photoUrls: Array.isArray(merged.photos)
          ? merged.photos.map((fn) => (fn ? uploadsUrl(`product/${fn}`) : "")).filter(Boolean).join(" | ")
          : "",
        preparationTime: merged.preparationTime ?? "",
        choiceIds: choiceRefs
          .map((c) => (typeof c === "object" && c?._id ? String(c._id) : String(c)))
          .filter(Boolean)
          .join(", "),
        choiceTitles: choiceRefs
          .map((c) => {
            const id = typeof c === "object" && c?._id ? String(c._id) : String(c);
            const doc = typeof c === "object" && c?.choiceDetails ? c : byChoiceId.get(id);
            return doc ? joinLangDetails(doc.choiceDetails, "choiceTitle") : id;
          })
          .filter(Boolean)
          .join(" || "),
      });
    }

    const subs = Array.isArray(cat.subcategories) ? cat.subcategories : [];
    for (const subWrap of subs) {
      const sub = subWrap.subCategory;
      if (!sub) continue;
      const subTitles = joinLangDetails(sub.subCategoryDetails, "title");
      const subId = String(sub._id || "");
      const subOrder = subWrap.order ?? "";

      categoryRows.push({
        menuId,
        menuNameEN: menuNameEn,
        categoryOrder: catOrder,
        categoryId: catId,
        categoryTitlesAllLangs: catTitles,
        subcategoryOrder: subOrder,
        subcategoryId: subId,
        subcategoryTitlesAllLangs: subTitles,
        placement: "subcategory",
      });

      const subProducts = Array.isArray(sub.productList) ? sub.productList : [];
      for (const pl of subProducts) {
        const pop = unwrapProduct(pl.product);
        const pid = productIdOf(pop);
        if (!pid) continue;
        const catalog = byProductId.get(pid) || {};
        const merged = { ...pop, ...catalog };
        const order = pl.order ?? "";

        const choiceRefs = Array.isArray(merged.choices) ? merged.choices : [];
        if (Array.isArray(merged.photos)) {
          merged.photos.forEach((fn, idx) => {
            if (fn) images.add(`product/${fn}`, pid, `${pid}_photo_${idx}_${fn}`);
          });
        }

        productRows.push({
          menuId,
          menuNameEN: menuNameEn,
          placement: "Subcategory",
          categoryOrder: catOrder,
          categoryId: catId,
          categoryTitles: catTitles,
          subcategoryOrder: subOrder,
          subcategoryId: subId,
          subcategoryTitles: subTitles,
          productOrder: order,
          productId: pid,
          productTitlesAllLangs: joinLangDetails(merged.productDetails, "productTitle"),
          descriptionsAllLangs: joinLangDetails(merged.productDetails, "productDescription"),
          price: merged.price ?? "",
          rate: merged.rate ?? "",
          haveMultipleSize: !!merged.haveMultipleSize,
          extraSizes: formatExtraSizes(merged.extraSizes),
          tags: Array.isArray(merged.tags) ? merged.tags.join(", ") : "",
          photos: Array.isArray(merged.photos) ? merged.photos.join(", ") : "",
          photoUrls: Array.isArray(merged.photos)
            ? merged.photos.map((fn) => (fn ? uploadsUrl(`product/${fn}`) : "")).filter(Boolean).join(" | ")
            : "",
          preparationTime: merged.preparationTime ?? "",
          choiceIds: choiceRefs
            .map((c) => (typeof c === "object" && c?._id ? String(c._id) : String(c)))
            .filter(Boolean)
            .join(", "),
          choiceTitles: choiceRefs
            .map((c) => {
              const id = typeof c === "object" && c?._id ? String(c._id) : String(c);
              const doc = typeof c === "object" && c?.choiceDetails ? c : byChoiceId.get(id);
              return doc ? joinLangDetails(doc.choiceDetails, "choiceTitle") : id;
            })
            .filter(Boolean)
            .join(" || "),
        });
      }
    }
  }

  return { categoryRows, productRows };
}

function choiceRowsForExcel(choicesList) {
  return choicesList.map((ch) => ({
    choiceId: String(ch._id || ""),
    titlesAllLangs: joinLangDetails(ch.choiceDetails, "choiceTitle"),
    minSelected: ch.minSelected ?? "",
    maxSelected: ch.maxSelected ?? "",
    obligatory: ch.obligatory ?? "",
    isFree: ch.isFree ?? "",
    isArchived: ch.isArchived ?? "",
    itemIds: Array.isArray(ch.items)
      ? ch.items.map((it) => (typeof it === "object" && it?._id ? String(it._id) : String(it))).join(", ")
      : "",
    rawJson: JSON.stringify(ch),
  }));
}

function itemRowsForExcel(itemsList, images) {
  return itemsList.map((it) => {
    const iid = String(it._id || "");
    if (it.photo) {
      images.add(`product/${it.photo}`, iid, it.photo);
    }
    return {
      itemId: iid,
      titlesAllLangs: joinLangDetails(it.itemDetails, "itemTitle"),
      photo: it.photo || "",
      photoUrl: it.photo ? uploadsUrl(`product/${it.photo}`) : "",
      price: it.price ?? "",
      havePrice: it.havePrice ?? "",
      isArchived: it.isArchived ?? "",
      rawJson: JSON.stringify(it),
    };
  });
}

async function fetchImageBlob(url) {
  const { data } = await axios.get(url, {
    responseType: "blob",
    timeout: 120000,
  });
  return data;
}

function downloadBlob(blob, filename) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function appendSheet(wb, rows, sheetName, emptyNote) {
  const data = rows.length ? rows : [{ note: emptyNote }];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), sanitizeSheetName(sheetName));
}

/**
 * Full restaurant backup: ZIP with data/restaurant_export.xlsx, data/raw/*.json,
 * images/{menu,category,product,items,user}/, README.txt
 * @param {{ userImage?: string }} options - profile image filename (as stored, e.g. abc.webp)
 */
export async function exportRestaurantFullZip(options = {}) {
  const { userImage } = options;
  const base = config.dev;
  const h = authHeaders();

  const [menusRes, catRes, prodRes, choiceRes, itemRes, subRes] = await Promise.all([
    axios.get(`${base}/v2/menu/getAll`, { headers: h }).catch((err) => {
      if (err.response?.status === 400) return { data: [] };
      throw err;
    }),
    axios.get(`${base}/v2/categories/retrieve/`, { headers: h }),
    axios.get(`${base}/v2/products/retrieve`, { headers: h }),
    axios.get(`${base}/v2/choices/retrieve`, { headers: h }),
    axios.get(`${base}/v2/items/retrieve`, { headers: h }),
    axios.get(`${base}/v2/subcategories/fetchAll`, { headers: h }),
  ]);

  const menusList = Array.isArray(menusRes.data) ? menusRes.data : [];
  const categoriesAll = Array.isArray(catRes.data) ? catRes.data : [];
  const productsAll = Array.isArray(prodRes.data) ? prodRes.data : [];
  const choicesAll = Array.isArray(choiceRes.data) ? choiceRes.data : [];
  const itemsAll = Array.isArray(itemRes.data) ? itemRes.data : [];
  const subcatsAll = Array.isArray(subRes.data) ? subRes.data : [];

  const byProductId = new Map();
  for (const p of productsAll) {
    if (p?._id) byProductId.set(String(p._id), p);
  }
  const byChoiceId = new Map();
  for (const c of choicesAll) {
    if (c?._id) byChoiceId.set(String(c._id), c);
  }
  const images = new HierarchyImageCollector();

  const menuSummaryRows = menusList.map((m) => ({
    menuId: String(m._id || ""),
    menuNameEN: menuNameEnOf(m),
    menuNamesAllLangs: joinLangDetails(m.menuDetails, "menuName"),
    photo: m.photo || "",
    photoUrl: m.photo ? uploadsUrl(`menu/${m.photo}`) : "",
    isEnabled: m.isEnabled !== false,
    displayOrder: m.displayOrder ?? "",
    start_date: m.start_date ? String(m.start_date) : "",
    end_date: m.end_date ? String(m.end_date) : "",
    start_time: m.start_time || "",
    end_time: m.end_time || "",
    day_of_week: Array.isArray(m.day_of_week) ? m.day_of_week.join(", ") : "",
    isArchived: !!m.isArchived,
  }));

  for (const m of menusList) {
    if (m.photo) images.add(`menu/${m.photo}`, String(m._id), m.photo);
  }

  for (const cat of categoriesAll) {
    const cid = String(cat._id || "");
    if (cat.photo) images.add(`category/${cat.photo}`, cid, cat.photo);
  }

  for (const p of productsAll) {
    const pid = String(p._id || "");
    if (Array.isArray(p.photos)) {
      p.photos.forEach((fn, idx) => {
        if (fn) images.add(`product/${fn}`, pid, `${pid}_photo_${idx}_${fn}`);
      });
    }
  }

  if (userImage) {
    images.add(`user/${userImage}`, "profile", userImage);
  }

  const menusFull = [];
  for (const m of menusList) {
    const id = m._id;
    if (!id) continue;
    try {
      const r = await axios.get(`${base}/v2/menu/getByID/${id}`, { headers: h });
      menusFull.push(r.data);
    } catch (e) {
      menusFull.push({ _id: id, _fetchError: String(e?.message || e) });
    }
  }

  const structureCategoryRows = [];
  const structureProductRows = [];
  for (const menu of menusFull) {
    if (menu._fetchError) continue;
    const { categoryRows, productRows } = walkMenuStructure(menu, byProductId, byChoiceId, images);
    structureCategoryRows.push(...categoryRows);
    structureProductRows.push(...productRows);
  }

  const choiceRows = choiceRowsForExcel(choicesAll);
  const itemRows = itemRowsForExcel(itemsAll, images);

  const imageManifestRows = images.tasks.map((t) => ({
    zipPath: t.zipPath,
    sourceUrl: t.url,
    entityType: t.entityType,
    entityId: t.entityId,
    sourceFile: t.sourceFile,
    downloadStatus: "pending",
  }));

  const zip = new JSZip();
  const dataFolder = zip.folder("data");
  const rawFolder = dataFolder.folder("raw");

  rawFolder.file("menus_full.json", JSON.stringify(menusFull, null, 2));
  rawFolder.file("categories.json", JSON.stringify(categoriesAll, null, 2));
  rawFolder.file("subcategories.json", JSON.stringify(subcatsAll, null, 2));
  rawFolder.file("products.json", JSON.stringify(productsAll, null, 2));
  rawFolder.file("choices.json", JSON.stringify(choicesAll, null, 2));
  rawFolder.file("items.json", JSON.stringify(itemsAll, null, 2));

  zip.file(
    "README.txt",
    [
      "Restaurant full export",
      "===================",
      "",
      "data/restaurant_export.xlsx — spreadsheets (menus, structure, catalogs, image manifest).",
      "data/raw/*.json — full API-shaped documents for restore or tooling.",
      "images/menu, images/category, images/product, images/items, images/user — downloaded media (when CORS/network allow).",
      "",
      "If some images are missing, see images/_FAILED_DOWNLOADS.txt and the ImageFiles sheet (sourceUrl).",
    ].join("\n")
  );

  const manifestFinal = imageManifestRows.map((r) => ({ ...r }));
  const failedLines = [];
  const concurrency = 5;
  const tasks = images.tasks;

  for (let i = 0; i < tasks.length; i += concurrency) {
    const chunk = tasks.slice(i, i + concurrency);
    await Promise.all(
      chunk.map(async (task) => {
        const manifestRow = manifestFinal.find((m) => m.zipPath === task.zipPath);
        try {
          const blob = await fetchImageBlob(task.url);
          zip.file(task.zipPath, blob);
          if (manifestRow) manifestRow.downloadStatus = "ok";
        } catch (e) {
          const msg = `${task.url} → ${e?.message || e}`;
          failedLines.push(msg);
          if (manifestRow) manifestRow.downloadStatus = `failed: ${e?.message || String(e)}`;
        }
      })
    );
  }

  if (failedLines.length) {
    zip.file(
      "images/_FAILED_DOWNLOADS.txt",
      `Some images could not be downloaded (often CORS or network). Re-download manually using URLs in data/restaurant_export.xlsx (sheet ImageFiles).\n\n${failedLines.join("\n")}`
    );
  }

  const wb = XLSX.utils.book_new();
  appendSheet(wb, menuSummaryRows, "Menus", "No menus");
  appendSheet(wb, structureCategoryRows, "Menu_Categories", "No category rows");
  appendSheet(wb, structureProductRows, "Menu_Products", "No products in menus");
  appendSheet(wb, categoriesAll.map(flattenDoc), "Categories_Catalog", "No categories");
  appendSheet(wb, subcatsAll.map(flattenDoc), "Subcategories", "No subcategories");
  appendSheet(wb, productsAll.map(flattenDoc), "Products_Catalog", "No products");
  appendSheet(wb, choiceRows, "Choices", "No choices");
  appendSheet(wb, itemRows, "Items", "No items");
  appendSheet(wb, manifestFinal, "ImageFiles", "No images");

  const xlsxArray = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  dataFolder.file("restaurant_export.xlsx", xlsxArray);

  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const zipName = `restaurant-full-backup-${stamp}.zip`;
  const zipBlob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
  downloadBlob(zipBlob, zipName);
}
